import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, AlertCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { backendUrl } from './gameplanUtils';
import { TurnaroundStrip } from './TurnaroundStrip';
import { useFiles, FileRow, RowBtn, ListHeader, TriageButtons, GroupBlock, SearchBox, filterFiles, Spinner, fmtD, money } from './shared/FileList';

// Files that are lodged and still waiting on the lender for a decision. When a file
// is approved / declined / withdrawn it leaves these columns, so it automatically
// drops out of the approval-age alerts below.
const AWAITING_APPROVAL = ['awaiting_approval', 'mir_actioning', 'mir_returned'];
const daysSince = (v) => { if (!v) return null; const d = new Date(v); const t = new Date(); d.setHours(0, 0, 0, 0); t.setHours(0, 0, 0, 0); return Math.floor((t - d) / 86400000); };
const longDate = (v) => (v ? new Date(v).toLocaleDateString('en-AU', { day: 'numeric', month: 'long' }) : '—');

const ApprovalAgeAlert = ({ f, days, onOpen }) => {
  const red = days >= 20;
  const lodged = f.lodged_at || f.submitted_date;
  const status = f.file?.status_label || f.file?.stage_label || 'Waiting for Approval';
  const broker = f.broker || f.file?.owner || 'Unassigned';
  return (
    <div data-testid={`approval-alert-${f.id}`} onClick={() => onOpen(f)}
      className={cn('approval-alert cursor-pointer rounded-xl border bg-white px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3',
        red ? 'approval-alert--red border-rose-300' : 'approval-alert--amber border-amber-300')}>
      <span className="relative flex h-2.5 w-2.5 shrink-0">
        {red && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-70" />}
        <span className={cn('relative inline-flex h-2.5 w-2.5 rounded-full', red ? 'bg-rose-500' : 'bg-amber-500')} />
      </span>
      <div className="min-w-0 flex-1">
        <div className={cn('flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider', red ? 'text-rose-600' : 'text-amber-600')} data-testid={`approval-alert-badge-${f.id}`}>
          {red ? <AlertTriangle className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
          {red ? 'Urgent' : 'Attention'} — {days} days since lodgement
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-slate-900">
          <span className="font-bold">{f.client_name}</span>
          <span className="text-slate-300">|</span><span>{f.lender || '—'}</span>
          <span className="text-slate-300">|</span><span className="text-slate-600">Lodged {longDate(lodged)}</span>
          <span className="text-slate-300">|</span><span className="text-slate-600">{status}</span>
        </div>
        <div className="mt-0.5 text-[11px] text-slate-500">Broker: <span className="font-semibold text-slate-600">{broker}</span> · {money(f.est_loan_amount)}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
        <span className={cn('rounded-full px-2.5 py-1 text-xs font-black tabular-nums', red ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700')}>{days}d</span>
        <RowBtn tone="dark" onClick={() => onOpen(f)} testid={`approval-alert-open-${f.id}`}>Open file</RowBtn>
      </div>
    </div>
  );
};

// Lodged date plus a day counter: orange from 15 days, flashing red from 20.
const DaysTag = ({ f, g }) => {
  const lodged = f.lodged_at || f.submitted_date;
  const days = AWAITING_APPROVAL.includes(g) ? daysSince(lodged) : null;
  return (
    <span className="inline-flex items-center gap-1.5">
      {fmtD(lodged)}
      {days != null && (
        <span data-testid={`days-tag-${f.id}`} className={cn('rounded-full px-2 py-0.5 text-[11px] font-black tabular-nums',
          days >= 20 ? 'bg-rose-100 text-rose-700 approval-alert--red' : days >= 15 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600')}>{days}d</span>
      )}
    </span>
  );
};

const GROUPS = [
  ['ready_to_lodge', 'Ready To Lodge', 'Packaged & ready — lodge now'],
  ['mir_actioning', 'MIR Needs Actioning', 'Lender asked for info — action on our side'],
  ['mir_returned', 'MIR Returned', 'Sent back to lender — awaiting reassessment'],
  ['awaiting_approval', 'Awaiting Approval', 'Lodged & sitting with the lender'],
  ['approved', 'Approved', 'Handing over to settlements'],
];
const IN_FLIGHT = GROUPS.map(([g]) => g);

const BulkDialog = ({ mode, deals, onClose, authToken, onDone }) => {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const total = deals.reduce((s, d) => s + Number(d.est_loan_amount || 0), 0);
  const go = async () => {
    setBusy(true);
    let ok = 0;
    for (const d of deals) {
      const body = mode === 'approve' ? { status_stage: 'approved', approved_date: date, approved_amount: d.est_loan_amount } : { status_stage: 'declined', decline_reason: reason || 'Not proceeding' };
      const r = await fetch(`${backendUrl}/api/gameplan/deals/${d.id}`, { method: 'PATCH', headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (r.ok) ok += 1;
    }
    setBusy(false);
    toast.success(mode === 'approve' ? `${ok} deal${ok !== 1 ? 's' : ''} approved` : `${ok} marked not proceeding`);
    onDone(); onClose();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="bulk-dialog">
        <DialogHeader><DialogTitle>{mode === 'approve' ? 'Approve' : 'Not proceeding'} — {deals.length} file{deals.length !== 1 ? 's' : ''} · {money(total)}</DialogTitle></DialogHeader>
        <ul className="max-h-40 overflow-auto text-sm text-slate-700 space-y-0.5">{deals.map((d) => <li key={d.id}>{d.client_name} · {money(d.est_loan_amount)} · {d.lender || '—'}</li>)}</ul>
        {mode === 'approve' ? <label className="block text-xs font-semibold text-slate-600">Approved date<Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1" data-testid="bulk-date" /></label>
          : <label className="block text-xs font-semibold text-slate-600">Reason (optional)<Input value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1" placeholder="e.g. Client went elsewhere" data-testid="bulk-reason" /></label>}
        <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={go} disabled={busy} className="rounded-full bg-slate-900 text-white" data-testid="bulk-confirm-btn">{busy ? 'Working…' : 'Confirm'}</Button></div>
      </DialogContent>
    </Dialog>
  );
};

export const ApprovalsView = ({ authToken, readOnly = false, onOpenFile, refreshSignal, onChanged }) => {
  const [files, reload] = useFiles(authToken, refreshSignal);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [bulk, setBulk] = useState(null);
  const [showStale, setShowStale] = useState(false);
  const [, setTick] = useState(0);
  // Roll the day count over automatically without a manual refresh.
  useEffect(() => { const id = setInterval(() => setTick((t) => t + 1), 3600000); return () => clearInterval(id); }, []);
  const done = () => { reload(); onChanged?.(); setSelected(new Set()); };

  const rows = useMemo(() => filterFiles(files || [], q), [files, q]);
  const byGroup = (g) => rows.filter((f) => f.file.col === g);
  const stale = byGroup('stale');
  const inFlight = rows.filter((f) => IN_FLIGHT.includes(f.file.col));
  // Oldest-first list of lodged files that have been waiting 15+ days for a lender decision.
  const ageAlerts = useMemo(() => rows
    .filter((f) => AWAITING_APPROVAL.includes(f.file.col))
    .map((f) => ({ f, days: daysSince(f.lodged_at || f.submitted_date) }))
    .filter((x) => x.days != null && x.days >= 15)
    .sort((a, b) => b.days - a.days), [rows]);
  const urgentCount = ageAlerts.filter((x) => x.days >= 20).length;
  const attentionCount = ageAlerts.length - urgentCount;
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectedDeals = rows.filter((f) => selected.has(f.id));
  if (!files) return <Spinner />;

  const rowActions = (f) => readOnly ? null : (f.file.col === 'stale' ? <TriageButtons f={f} authToken={authToken} onDone={done} /> : (<>
    {['awaiting_approval', 'mir_actioning', 'mir_returned'].includes(f.file.col) && <RowBtn tone="lime" onClick={() => setBulk({ mode: 'approve', deals: [f] })} testid={`approve-${f.id}`}>Approve</RowBtn>}
    {f.file.col !== 'approved' && <RowBtn tone="danger" onClick={() => setBulk({ mode: 'decline', deals: [f] })} testid={`decline-${f.id}`}>Not proceeding</RowBtn>}
  </>));

  return (
    <div className="space-y-4" data-testid="approvals-view">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div><h2 className="text-lg font-extrabold text-slate-900">Approvals</h2><p className="text-xs text-slate-500">Lodged → lender decision · orange from 15 days, red from 20 · {inFlight.length} files in flight · {money(inFlight.reduce((s, f) => s + Number(f.est_loan_amount || 0), 0))}</p></div>
        <SearchBox q={q} setQ={setQ} testid="approvals-search" />
      </div>
      <TurnaroundStrip authToken={authToken} refreshSignal={refreshSignal} />
      {ageAlerts.length > 0 && (
        <section className="rounded-xl border border-slate-200 bg-slate-50/70 p-3" data-testid="approval-age-alerts">
          <div className="flex items-baseline justify-between px-1 pb-2">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-rose-600" />
              <span className="text-sm font-extrabold text-slate-900">Overdue for approval</span>
              <span className="text-xs text-slate-500">oldest first · action these before working the list</span>
            </div>
            <div className="text-xs tabular-nums text-slate-600">
              {urgentCount > 0 && <span className="font-bold text-rose-700">{urgentCount} urgent</span>}
              {urgentCount > 0 && attentionCount > 0 && <span className="text-slate-300"> · </span>}
              {attentionCount > 0 && <span className="font-bold text-amber-700">{attentionCount} attention</span>}
            </div>
          </div>
          <div className="space-y-2">
            {ageAlerts.map(({ f, days }) => <ApprovalAgeAlert key={f.id} f={f} days={days} onOpen={onOpenFile} />)}
          </div>
        </section>
      )}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 rounded-xl bg-[#0f172a] cp-dark text-white px-4 py-2.5" data-testid="bulk-toolbar">
          <span className="text-sm font-bold">{selected.size} selected · {money(selectedDeals.reduce((s, d) => s + Number(d.est_loan_amount || 0), 0))}</span>
          <RowBtn tone="lime" onClick={() => setBulk({ mode: 'approve', deals: selectedDeals })} testid="bulk-approve-btn">Approve selected</RowBtn>
          <RowBtn tone="light" onClick={() => setBulk({ mode: 'decline', deals: selectedDeals })} testid="bulk-decline-btn">Not proceeding</RowBtn>
          <button onClick={() => setSelected(new Set())} className="ml-auto text-xs text-white/70 hover:text-white" data-testid="bulk-clear-btn">Clear</button>
        </div>
      )}
      {GROUPS.map(([g, title, sub]) => {
        const items = byGroup(g);
        if (!items.length) return null;
        return (
          <GroupBlock key={g} title={title} sub={sub} count={items.length} value={items.reduce((s, f) => s + Number(f.est_loan_amount || 0), 0)} testid={`group-${g}`}>
            <ListHeader dateLabel="Lodged" selectAll={readOnly ? null : <input type="checkbox" className="w-4 h-4 accent-slate-900" checked={items.every((f) => selected.has(f.id))} onChange={(e) => setSelected((s) => { const n = new Set(s); items.forEach((f) => e.target.checked ? n.add(f.id) : n.delete(f.id)); return n; })} data-testid={`select-all-${g}`} />} />
            {items.map((f) => <FileRow key={f.id} f={f} onOpen={onOpenFile} dateLabel="" dateValue={<DaysTag f={f} g={g} />} actions={rowActions(f)} selected={selected.has(f.id)} onSelect={readOnly ? undefined : toggle} />)}
          </GroupBlock>
        );
      })}
      {inFlight.length === 0 && <div className="rounded-xl bg-white border border-slate-200 px-6 py-10 text-center text-sm text-slate-500" data-testid="approvals-empty">No lodged files in flight.</div>}
      {stale.length > 0 && (
        <GroupBlock title="Needs triage" sub="entered before the workflow existed and never updated — confirm what happened" count={stale.length} value={stale.reduce((s, f) => s + Number(f.est_loan_amount || 0), 0)} testid="group-stale" tone="muted">
          {!showStale ? <button onClick={() => setShowStale(true)} className="w-full py-3 text-xs font-bold text-slate-700 hover:bg-slate-50" data-testid="show-stale">Show {stale.length} files to triage</button> : (<>
            <ListHeader dateLabel="Entered" selectAll={readOnly ? null : <input type="checkbox" className="w-4 h-4 accent-slate-900" checked={stale.every((f) => selected.has(f.id))} onChange={(e) => setSelected((s) => { const n = new Set(s); stale.forEach((f) => e.target.checked ? n.add(f.id) : n.delete(f.id)); return n; })} data-testid="select-all-stale" />} />
            {stale.map((f) => <FileRow key={f.id} f={f} onOpen={onOpenFile} dateLabel="" dateValue={fmtD(f.submitted_date)} actions={rowActions(f)} selected={selected.has(f.id)} onSelect={readOnly ? undefined : toggle} />)}
          </>)}
        </GroupBlock>
      )}
      {bulk && <BulkDialog mode={bulk.mode} deals={bulk.deals} onClose={() => setBulk(null)} authToken={authToken} onDone={done} />}
    </div>
  );
};

export default ApprovalsView;
